import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Mail, ShieldCheck, Cog, PackageCheck } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import Marquee from "../components/Marquee";
import { IMAGES, CONTACT } from "../data/site";

const MACHINERY = ["Kneader", "Extruder", "Calendaring Machine", "Mixing Mill", "Curing Press"];

const About = () => (
  <>
    <section className="relative pt-32 sm:pt-40 pb-16 overflow-hidden" data-testid="about-hero">
      <div className="absolute inset-0 blueprint-grid" />
      <div className="relative mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 grid gap-12 lg:grid-cols-[1.3fr_1fr] items-end">
        <div>
          <Reveal y={16}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">About Maximus</p>
          </Reveal>
          <MaskedLines
            delay={0.1}
            lines={["RUBBER, ENGINEERED", { node: <span className="text-outline">IN TRIPURA</span> }]}
            className="mt-4 font-display font-black uppercase leading-[0.9] tracking-tight text-[clamp(2.8rem,7.5vw,7rem)]"
          />
        </div>
        <Reveal delay={0.4} y={16} className="lg:pb-4">
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
            {CONTACT.company} — manufacturing {CONTACT.tagline.toLowerCase()} for customers who measure
            value in kilometres.
          </p>
        </Reveal>
      </div>
    </section>

    <Marquee items={["Best Raw Materials", "High Technology", "Stage-Wise Quality Checks", "Radial & Bias"]} />

    <section className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 py-20 lg:py-28" data-testid="about-story">
      <div className="grid gap-14 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">Company Profile</p>
            <p className="mt-6 font-display text-2xl sm:text-4xl font-semibold leading-snug text-white">
              Maximus Treads are manufactured in the State of Tripura, equipped with the latest kneader,
              extruder, calendaring machine, mixing mill and curing press.
            </p>
            <p className="mt-8 max-w-xl text-base text-slate-400 leading-relaxed">
              We initiate quality checks at each and every stage of our production, starting from mixing,
              extrusion and curing. We use the best raw materials in the market along with high technology —
              and by providing the best performance precured tread rubber, we help our customers achieve high
              mileage. We manufacture all types of radial & bias precured tread rubber.
            </p>
          </Reveal>

          <Reveal delay={0.15} className="mt-10 flex flex-wrap gap-2">
            {MACHINERY.map((m) => (
              <span key={m} className="border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300" data-testid="machinery-chip">
                {m}
              </span>
            ))}
          </Reveal>

          <Reveal delay={0.2} className="mt-12 grid gap-5 sm:grid-cols-3">
            {[
              { icon: PackageCheck, t: "Raw Materials", c: "The best in the market, selected for compound integrity." },
              { icon: Cog, t: "Technology", c: "Latest plant equipment, run to tight process control." },
              { icon: ShieldCheck, t: "Quality Checks", c: "At every stage — mixing, extrusion and curing." },
            ].map((f) => (
              <div key={f.t} className="border-t-2 border-amber pt-5">
                <f.icon size={20} className="text-amber" />
                <h3 className="mt-3 font-display text-xl font-bold uppercase tracking-tight text-white">{f.t}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.c}</p>
              </div>
            ))}
          </Reveal>
        </div>

        <div className="space-y-6">
          <Reveal delay={0.1}>
            <div className="clip-corner overflow-hidden">
              <img src={IMAGES.factory1} alt="Worker at the Maximus rubber plant" className="h-72 sm:h-96 w-full object-cover" data-testid="about-image-1" />
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="bg-signal p-8" data-testid="rubber-board-band">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">Official Guidance</p>
              <p className="mt-3 font-display text-2xl sm:text-3xl font-bold uppercase leading-tight text-white">
                Under the Technical Guidance of the Rubber Board, Govt. of India
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="border border-white/10 bg-panel/50 p-8" data-testid="about-location-card">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">The Plant</p>
              <p className="mt-4 flex gap-3 text-sm text-slate-300 leading-relaxed">
                <MapPin size={16} className="text-amber shrink-0 mt-0.5" />
                <span>{CONTACT.addressLines[0]},<br />{CONTACT.addressLines[1]}</span>
              </p>
              <div className="mt-6 space-y-2">
                {CONTACT.phones.map((p) => (
                  <a key={p} href={`tel:+91${p}`} className="flex gap-3 text-sm text-slate-300 hover:text-white transition-colors" data-testid={`about-phone-${p}`}>
                    <Phone size={16} className="text-amber shrink-0" /> +91 {p}
                  </a>
                ))}
                <a href={`mailto:${CONTACT.email}`} className="flex gap-3 text-sm text-slate-300 hover:text-white transition-colors break-all" data-testid="about-email">
                  <Mail size={16} className="text-amber shrink-0" /> {CONTACT.email}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="bg-[#F8FAFC] text-deep" data-testid="about-cta">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 py-20 lg:py-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tight leading-[0.95]">
            Put MAXIMUS rubber<br />on your fleet
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/contact"
            data-testid="about-cta-quote-button"
            className="group inline-flex items-center gap-3 bg-signal px-10 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white hover:bg-deep transition-colors"
          >
            Request a Quote
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  </>
);

export default About;
